<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useWorkspaceStore } from '@stores/WorkspaceData';
import Chart from 'primevue/chart';
import TieredMenu from 'primevue/tieredmenu';
import Select from 'primevue/select';
import DatePicker from 'primevue/datepicker';
import { unixToDate, dateToUnix } from '@services/process/Main/unix.servise';
const wpStore = useWorkspaceStore()

// Формы анализа выходных данных (съёмка цели) — раздел 5.1 описания ЭПК.
// Отдельно выбираются интервал отрисовки (селектор + Begin / End) и тип графика (меню).
const Result = computed(() => wpStore.simulation)
const Points = computed(() => Result.value?.points || [])

const EARTH_RADIUS = 6371
// Цвета как в описании форм: тангаж — красный, крен — синий, рыскание — зелёный
const COLORS = {roll: '#3b82f6', pitch: '#ef4444', yaw: '#22c55e', norm: '#8b5cf6', main: '#ef4444', limit: '#3b82f6'}

const CHARTS = [
  {key: 'orientation', label: 'Ориентация КА', icon: 'pi pi-compass'},
  {key: 'deviation', label: 'Отклонения в плоскостях крена и тангажа', icon: 'pi pi-arrows-v'},
  {key: 'rate', label: 'Скорость углового вращения', icon: 'pi pi-refresh'},
  {key: 'angle', label: 'Отклонение по модулю угла', icon: 'pi pi-bullseye'},
  {key: 'distance', label: 'Отклонения по расстоянию', icon: 'pi pi-arrows-h'},
]
const chartKey = ref('orientation')
const chartInfo = computed(() => CHARTS.find(c => c.key === chartKey.value))

// Меню типов графика; выбранный отмечен галочкой
const options = computed(() => CHARTS.map(chart => ({
  label: chart.label,
  icon: chartKey.value === chart.key ? 'pi pi-check' : chart.icon,
  class: chartKey.value === chart.key ? 'ChartMenuActive' : '',
  command: () => { chartKey.value = chart.key },
})))
const menu = ref()
const ToggleMenu = (event) => menu.value.toggle(event)

// Интервалы отрисовки. Съёмка — фактическая, по результату моделирования (цель в луче, r(t) < p):
// «Горизонт моделирования» — начало горизонта … окончание съёмки, «Съёмка» — начало … окончание съёмки,
// «Разворот» — начало разворота (t transfer) … начало съёмки
const presetRanges = computed(() => {
  const r = Result.value
  const last = Points.value.at(-1)?.time
  if(!r || last == null) return {}
  const actual = r.actualShooting
  return {
    horizon: [r.horizonStart ?? Points.value[0].time, actual?.end ?? last],
    shoot: actual ? [actual.start, actual.end] : null,
    turn: [r.turnaroundStart ?? Points.value[0].time, actual?.start ?? last],
  }
})
const PRESETS = [['horizon', 'Горизонт моделирования'], ['shoot', 'Съёмка'], ['turn', 'Разворот']]
// «Свой интервал» — неактивный пункт: показывается, когда Begin / End изменены вручную
const presetOptions = computed(() => [
  ...PRESETS.map(([value, label]) => ({value, label, disabled: !presetRanges.value[value]})),
  {value: 'custom', label: 'Свой интервал', disabled: true},
])

// Begin / End задаются выбором интервала и могут быть изменены вручную
const begin = ref(null)
const end = ref(null)
const ApplyPreset = (key) => {
  const range = presetRanges.value[key]
  if(!range) return
  begin.value = unixToDate(range[0])
  end.value = unixToDate(range[1])
}
// Какой интервал сейчас выставлен (сравнение с точностью до секунды — DatePicker отбрасывает доли);
// если Begin / End поменяли вручную — ни один, в селекторе «Свой интервал»
const activePreset = computed(() => PRESETS.map(([key]) => key).find(key => {
  const range = presetRanges.value[key]
  return range && Math.abs(dateToUnix(begin.value) - range[0]) < 1 && Math.abs(dateToUnix(end.value) - range[1]) < 1
}) ?? 'custom')
watch(Result, () => ApplyPreset('horizon'), {immediate: true})

// minDate/maxDate у DatePicker в пределах одного дня блокируют ввод времени,
// поэтому Begin > End просто меняем местами
const range = computed(() => {
  const from = dateToUnix(begin.value), to = dateToUnix(end.value)
  if(from == null || to == null) return []
  return [Math.min(from, to), Math.max(from, to)]
})
const visiblePoints = computed(() => {
  const [from, to] = range.value
  if(from == null) return []
  return Points.value.filter(p => p.time >= from && p.time <= to)
})

// Радиус поля захвата на Земле (расчётный): наклонная дальность до точки нацеливания × tan(p).
// Дальность — по высоте орбиты (большая полуось − R) и углу оси съёмки от надира (z орбитальной СК — надир)
const captureRadius = (point) => {
  const a = wpStore.experiment.Sat?.config?.orbit?.semiMajorAxis
  const c = point.aimingVector
  const p = Result.value?.beamAngle
  if(!a || !c || p == null) return null
  const norm = Math.hypot(c.x, c.y, c.z)
  const cosT = c.z / norm
  const sin2T = 1 - cosT * cosT
  const disc = EARTH_RADIUS ** 2 - a * a * sin2T
  if(cosT <= 0 || disc < 0) return null
  const slant = a * cosT - Math.sqrt(disc)
  return slant * Math.tan(p * Math.PI / 180)
}

// Кривые, скрытые щелчком по легенде, — отдельно для каждого графика. Храним сами, т.к. Chart.js держит
// это состояние внутри графика и теряет его при перестройке (смена интервала, графика, темы)
const hiddenSeries = ref({})
const ToggleSeries = (label) => {
  const key = `${chartKey.value}:${label}`
  hiddenSeries.value = {...hiddenSeries.value, [key]: !hiddenSeries.value[key]}
}
const series = (label, color, get, extra = {}) => ({
  label, borderColor: color, backgroundColor: color,
  data: visiblePoints.value.map(p => ({x: p.time, y: get(p) ?? null})),
  borderWidth: 2, pointRadius: 0, spanGaps: false,
  hidden: !!hiddenSeries.value[`${chartKey.value}:${label}`], ...extra,
})
const dashed = {borderDash: [6, 4]}

const chartData = computed(() => {
  const beam = Result.value?.beamAngle
  let unit = '', datasets = []
  switch(chartKey.value){
    case 'orientation': unit = 'Угол, °'; datasets = [
      series('Тангаж W(t)', COLORS.pitch, p => p.pitch, {column: 'Тангаж'}),
      series('Крен W(t)', COLORS.roll, p => p.roll, {column: 'Крен'}),
      series('Рыскание W(t)', COLORS.yaw, p => p.attitude?.yaw, {column: 'Рыскание'}),
      series('Тангаж WC(t) — видимость цели', COLORS.pitch, p => p.targetPitch, {...dashed, column: 'Тангаж — видимость цели'}),
      series('Крен WC(t) — видимость цели', COLORS.roll, p => p.targetRoll, {...dashed, column: 'Крен — видимость цели'}),
    ]; break
    case 'deviation': unit = 'Угол, °'; datasets = [
      series('Крен (da)', COLORS.roll, p => p.rollDeviation, {column: 'Крен'}),
      series('Тангаж (db)', COLORS.pitch, p => p.pitchDeviation, {column: 'Тангаж'}),
    ]; break
    case 'rate': unit = 'Угловая скорость, °/с'; datasets = [
      series('Тангаж (ось Y)', COLORS.pitch, p => p.angularRate?.y),
      series('Крен (ось X)', COLORS.roll, p => p.angularRate?.x),
      series('Рыскание (ось Z)', COLORS.yaw, p => p.angularRate?.z),
      series('Модуль', COLORS.norm, p => p.angularRateNorm, {borderWidth: 1, ...dashed}),
    ]; break
    case 'angle': unit = 'Угол, °'; datasets = [
      series('r(t) — угол между нацеливанием и видимостью цели', COLORS.main, p => p.deviationAngle, {column: 'Угол между нацеливанием и видимостью цели'}),
      series(`p — угол луча съёмки (${beam}°)`, COLORS.limit, () => beam, {borderWidth: 1.5, column: 'Угол луча съёмки'}),
    ]; break
    case 'distance': unit = 'Расстояние, км'; datasets = [
      series('d(t) — расстояние от цели до точки нацеливания', COLORS.main, p => p.distance, {column: 'Расстояние от цели до точки нацеливания'}),
      series('Радиус поля захвата (расчётный)', COLORS.limit, captureRadius, {borderWidth: 1.5}),
    ]; break
  }
  return {unit, datasets}
})
const hasData = computed(() => chartData.value.datasets.some(d => d.data.some(v => v.y != null)))

// Таблица значений под графиком: время + видимые кривые (скрытые в легенде не показываем).
// Заголовок столбца — column: название без обозначения переменной (W(t), da, r(t), …); иначе — подпись из легенды
const TABLE_ROWS = 20
const tableColumns = computed(() => chartData.value.datasets
  .map((d, index) => ({field: `s${index}`, header: d.column ?? d.label, hidden: d.hidden}))
  .filter(c => !c.hidden))
const tableRows = computed(() => visiblePoints.value.map((p, i) => {
  const row = {time: p.time}
  chartData.value.datasets.forEach((d, index) => { row[`s${index}`] = d.data[i]?.y })
  return row
}))
const tableFirst = ref(0)
// при смене графика или интервала — на первую страницу
watch([chartKey, range], () => { tableFirst.value = 0 })
const formatValue = (v) => v == null ? '—' : v.toLocaleString('ru-RU', {maximumFractionDigits: 4})
const formatDateTime = (sec) => new Date(sec * 1000).toLocaleString('ru-RU')

// Цвета осей и подписей берём из темы PrimeVue; при переключении темы пересчитываем
const themeVersion = ref(0)
let observer = null
onMounted(() => {
  observer = new MutationObserver(() => themeVersion.value++)
  observer.observe(document.documentElement, {attributes: true, attributeFilter: ['class']})
})
onUnmounted(() => observer?.disconnect())
const cssVar = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim()

const formatTime = (sec) => new Date(sec * 1000).toLocaleTimeString('ru-RU')
const chartOptions = computed(() => {
  themeVersion.value
  const text = cssVar('--p-text-color')
  const muted = cssVar('--p-text-muted-color')
  const grid = cssVar('--p-content-border-color')
  return {
    animation: false,
    maintainAspectRatio: false,
    parsing: false,
    interaction: {mode: 'index', intersect: false},
    plugins: {
      // Щелчок по элементу легенды скрывает / показывает кривую (скрытая — зачёркнута)
      legend: {position: 'bottom', labels: {color: text, usePointStyle: true, pointStyle: 'line'},
        onClick: (event, item, legend) => ToggleSeries(legend.chart.data.datasets[item.datasetIndex].label),
        onHover: (event) => { event.native.target.style.cursor = 'pointer' },
        onLeave: (event) => { event.native.target.style.cursor = '' }},
      tooltip: {callbacks: {
        title: (items) => items.length ? new Date(items[0].parsed.x * 1000).toLocaleString('ru-RU') : '',
        label: (item) => `${item.dataset.label}: ${item.parsed.y == null ? '—' : item.parsed.y.toLocaleString('ru-RU', {maximumFractionDigits: 4})}`,
      }},
    },
    scales: {
      x: {type: 'linear', min: range.value[0], max: range.value[1],
        ticks: {color: muted, maxTicksLimit: 8, callback: formatTime}, grid: {color: grid},
        title: {display: true, text: 'Время', color: muted}},
      y: {ticks: {color: muted}, grid: {color: grid},
        title: {display: true, text: chartData.value.unit, color: muted}},
    },
  }
})
</script>

<template>
<div class="ModellingCard SimulationCharts">
  <div class="ModellingCardTitle"><i class="pi pi-chart-bar"></i>Формы анализа выходных данных</div>
  <div class="ChartControls">
    <FloatLabel variant="on">
      <Select :modelValue="activePreset" @update:modelValue="ApplyPreset" :options="presetOptions"
        optionLabel="label" optionValue="value" optionDisabled="disabled" class="ChartPreset">
        <!-- Select не показывает подпись неактивного пункта «Свой интервал» — выводим её сами -->
        <template #value>{{ presetOptions.find(o => o.value === activePreset)?.label }}</template>
      </Select>
      <label>Интервал</label>
    </FloatLabel>
    <FloatLabel variant="on">
      <DatePicker v-model="begin" showTime showSeconds hourFormat="24" dateFormat="dd.mm.yy"/>
      <label>Begin</label>
    </FloatLabel>
    <FloatLabel variant="on">
      <DatePicker v-model="end" showTime showSeconds hourFormat="24" dateFormat="dd.mm.yy"/>
      <label>End</label>
    </FloatLabel>
  </div>
  <div class="ChartControls">
    <Button type="button" severity="secondary" variant="outlined" class="ChartSelect" aria-haspopup="true" aria-controls="chart_menu" @click="ToggleMenu">
      <i :class="chartInfo?.icon"></i>
      <span class="ChartSelectLabel">{{ chartInfo?.label }}</span>
      <i class="pi pi-chevron-down"></i>
    </Button>
    <TieredMenu ref="menu" id="chart_menu" :model="options" popup class="ChartMenu"/>
  </div>
  <div class="ChartBox">
    <Chart v-if="hasData" type="line" :data="{datasets: chartData.datasets}" :options="chartOptions" class="ChartCanvas"/>
    <p v-else class="ModellingEmpty">Нет данных моделирования в интервале Begin – End</p>
  </div>
  <small class="ChartNote">
    {{ chartInfo?.label }}: {{ visiblePoints.length }} точек.
    <template v-if="chartKey === 'distance'">Радиус поля захвата — оценка на фронтенде по высоте орбиты и углу луча.</template>
  </small>
  <!-- table-layout: fixed — столбцы данных одной ширины, длинные заголовки переносятся;
       минимальная ширина, чтобы при многих кривых столбцы не сжимались (тогда появится прокрутка) -->
  <DataTable v-if="hasData" :value="tableRows" v-model:first="tableFirst" :rows="TABLE_ROWS" paginator
    size="small" stripedRows scrollable class="ChartTable"
    :tableStyle="{tableLayout: 'fixed', minWidth: `${170 + tableColumns.length * 110}px`}"
    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
    currentPageReportTemplate="{first}–{last} из {totalRecords}">
    <Column field="time" header="Время" frozen class="ChartTableTime" headerClass="ChartTableTime"
      :headerStyle="{width: '170px'}">
      <template #body="{data}">{{ formatDateTime(data.time) }}</template>
    </Column>
    <Column v-for="col in tableColumns" :key="col.field" :field="col.field" :header="col.header" class="ChartTableValue">
      <template #body="{data}">{{ formatValue(data[col.field]) }}</template>
    </Column>
  </DataTable>
</div>
</template>

<style>
.SimulationCharts{
  grid-column: 1 / -1;
}
.ChartControls{
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}
.ChartSelect.p-button{
  min-width: min(100%, 420px);
  justify-content: flex-start;
  gap: 8px;
}
.ChartPreset{
  min-width: 230px;
}
.ChartSelectLabel{
  flex: 1;
  text-align: left;
}
.ChartMenu .ChartMenuActive > .p-tieredmenu-item-content{
  color: var(--p-primary-color);
  font-weight: 600;
}
.ChartMenu .ChartMenuActive > .p-tieredmenu-item-content .p-tieredmenu-item-icon{
  color: var(--p-primary-color);
}
.ChartBox{
  height: 380px;
}
.ChartCanvas{
  height: 100%;
}
.ChartNote{
  display: block;
  margin-top: 6px;
  color: var(--p-text-muted-color);
}
.ChartTable{
  margin-top: 10px;
}
.ChartTable td.ChartTableTime{
  white-space: nowrap;
}
.ChartTable th{
  vertical-align: bottom;
  white-space: normal;
  overflow-wrap: anywhere;
}
.ChartTable td.ChartTableValue{
  text-align: right;
  font-variant-numeric: tabular-nums;
}
</style>
